import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { FirmwareSnapshot } from 'app/agriculture/queries/devices/types/FirmwareSnapshot';
import { useContext } from 'react';
import { DeviceDetailContext } from 'app/agriculture/routes/Devices/DeviceDetails/context/DeviceDetailContextProvider';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';
import { SuspenseWrapper } from 'app/agriculture/components/SuspenseWrapper/SuspenseWrapper';
import { FirmwareDisplaySkeleton } from 'app/agriculture/routes/Devices/DeviceDetails/components/display/FirmwareDisplaySkeleton';

export function FirmwareDisplay() {
    return (
        <SuspenseWrapper loadingComponent={<FirmwareDisplaySkeleton />}>
            <WrappedFirmwareDisplay />
        </SuspenseWrapper>
    );
}

function WrappedFirmwareDisplay() {
    const { device } = useContext(DeviceDetailContext);
    const { timestamp, packageName, packageVersion, state, updateDeliveryMethod, updateResult } =
        useSnapshot({
            snapshotType: 'Firmware',
            parentId: device.id,
            shelfLife: null,
        }) ?? {};

    return (
        <ContentBox.Section heading="Latest Firmware Update" disableDivider timestamp={timestamp}>
            <SimpleGrid className="FirmwareDisplay" columns={3}>
                <StatDisplay name="Package name" value={packageName} />
                <StatDisplay name="Package version" value={packageVersion} />
                <StatDisplay name="State" value={stateCodeToString({ state })} />
                <StatDisplay
                    name="Update delivery method"
                    value={updateDeliveryMethodCodeToString({ updateDeliveryMethod })}
                />
                <StatDisplay
                    name="Update result"
                    value={updateResultCodeToString({ updateResult })}
                />
            </SimpleGrid>
        </ContentBox.Section>
    );
}

function stateCodeToString({ state }: Pick<Partial<FirmwareSnapshot>, 'state'>) {
    if (state == null) return null;

    switch (state) {
        case 0:
            return 'Idle';
        case 1:
            return 'Downloading';
        case 2:
            return 'Downloaded';
        default:
            return state.toString();
    }
}

function updateDeliveryMethodCodeToString({
    updateDeliveryMethod,
}: Pick<Partial<FirmwareSnapshot>, 'updateDeliveryMethod'>) {
    if (updateDeliveryMethod == null) return null;

    switch (updateDeliveryMethod) {
        case 0:
            return 'Pull only';
        case 1:
            return 'Push only';
        case 2:
            return 'Push + Pull';
        default:
            return updateDeliveryMethod.toString();
    }
}

function updateResultCodeToString({
    updateResult,
}: Pick<Partial<FirmwareSnapshot>, 'updateResult'>) {
    if (updateResult == null) return null;

    switch (updateResult) {
        case 0:
            return 'Initial value';
        case 1:
            return 'Firmware updated successfully';
        case 2:
            return 'Not enough flash memory for the new firmware package';
        case 3:
            return 'Out of RAM during downloading process';
        case 4:
            return 'Connection lost during downloading process';
        case 5:
            return 'Integrity check failure for new downloaded package';
        case 6:
            return 'Unsupported package type';
        case 7:
            return 'Invalid URI';
        case 8:
            return 'Firmware update failed';
        case 9:
            return 'Unsupported protocol';
        default:
            return updateResult.toString();
    }
}
